import { createFileRoute } from '@tanstack/react-router';
import {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, RotateCcw } from 'lucide-react';
import {
  describeFeeReminder,
  FEE_DUE_DAY_OF_MONTH,
  FEE_REMINDER_MAX,
  FEE_REMINDER_MAX_DAYS_AFTER,
  FEE_REMINDER_MAX_DAYS_BEFORE,
  feeReminderCode,
  feeReminderWarning,
  parseFeeReminder,
  sortFeeReminders,
  systemSettingsUpdateSchema,
  type FeeReminder,
  type SystemSettingsUpdate,
} from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useMenuPath } from '../lib/menu-path';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { TextField } from '../components/ui/Field';
import { parseJsonField, type SystemSettingsRecord } from '../components/system/types';
import { EMAIL_STATUS_KEY, EmailStatusPanel } from '../components/system/EmailStatus';

export const Route = createFileRoute('/_authed/system/settings')({
  component: SystemSettingsPage,
});

interface FormValues {
  autoGenerateFeeCyclesEnabled: boolean;
  autoGenerateFeeCyclesDayOfMonth: number;
  autoGenerateSalaryCyclesEnabled: boolean;
  autoGenerateSalaryCyclesDayOfMonth: number;
  autoFeeRemindersEnabled: boolean;
  /** Stored reminder codes ("T-7", "overdue+7"); always shown in plain words. */
  autoFeeReminderSchedule: string[];
  onlinePaymentsEnabled: boolean;
  onlinePaymentsProvider: 'none' | 'razorpay';
  siblingDiscountPercent: number;
  /** Rupees in the UI; converted to paise on submit. */
  lateFeeRupees: number;
  lateFeeGraceDays: number;
  feeDueDayOfMonth: number;
  automaticEmailsEnabled: boolean;
}

const toFormValues = (raw: SystemSettingsRecord): FormValues => {
  const schedule =
    parseJsonField<string[]>(raw.autoFeeReminderSchedule) ?? [];
  return {
    autoGenerateFeeCyclesEnabled: raw.autoGenerateFeeCyclesEnabled,
    autoGenerateFeeCyclesDayOfMonth: raw.autoGenerateFeeCyclesDayOfMonth,
    autoGenerateSalaryCyclesEnabled: raw.autoGenerateSalaryCyclesEnabled,
    autoGenerateSalaryCyclesDayOfMonth: raw.autoGenerateSalaryCyclesDayOfMonth,
    autoFeeRemindersEnabled: raw.autoFeeRemindersEnabled,
    autoFeeReminderSchedule: sortFeeReminders(schedule.filter(isReminderItem)),
    onlinePaymentsEnabled: raw.onlinePaymentsEnabled,
    onlinePaymentsProvider: raw.onlinePaymentsProvider,
    siblingDiscountPercent: raw.siblingDiscountPercent,
    lateFeeRupees: Math.round(raw.lateFeeAmountPaise / 100),
    lateFeeGraceDays: raw.lateFeeGraceDays,
    feeDueDayOfMonth: raw.feeDueDayOfMonth ?? FEE_DUE_DAY_OF_MONTH,
    automaticEmailsEnabled: raw.automaticEmailsEnabled ?? false,
  };
};

const isReminderItem = (value: unknown): value is string => typeof value === 'string' && parseFeeReminder(value) !== null;

const arraysEqual = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

/**
 * Diff a form snapshot against the initial values and return a partial
 * {@link SystemSettingsUpdate} containing only the changed fields.
 */
const buildPatch = (initial: FormValues, next: FormValues): SystemSettingsUpdate => {
  const patch: SystemSettingsUpdate = {};
  if (initial.autoGenerateFeeCyclesEnabled !== next.autoGenerateFeeCyclesEnabled)
    patch.autoGenerateFeeCyclesEnabled = next.autoGenerateFeeCyclesEnabled;
  if (initial.autoGenerateFeeCyclesDayOfMonth !== next.autoGenerateFeeCyclesDayOfMonth)
    patch.autoGenerateFeeCyclesDayOfMonth = next.autoGenerateFeeCyclesDayOfMonth;
  if (initial.autoGenerateSalaryCyclesEnabled !== next.autoGenerateSalaryCyclesEnabled)
    patch.autoGenerateSalaryCyclesEnabled = next.autoGenerateSalaryCyclesEnabled;
  if (initial.autoGenerateSalaryCyclesDayOfMonth !== next.autoGenerateSalaryCyclesDayOfMonth)
    patch.autoGenerateSalaryCyclesDayOfMonth = next.autoGenerateSalaryCyclesDayOfMonth;
  if (initial.autoFeeRemindersEnabled !== next.autoFeeRemindersEnabled)
    patch.autoFeeRemindersEnabled = next.autoFeeRemindersEnabled;
  if (!arraysEqual(initial.autoFeeReminderSchedule, next.autoFeeReminderSchedule))
    patch.autoFeeReminderSchedule = next.autoFeeReminderSchedule;
  if (initial.onlinePaymentsEnabled !== next.onlinePaymentsEnabled)
    patch.onlinePaymentsEnabled = next.onlinePaymentsEnabled;
  if (initial.onlinePaymentsProvider !== next.onlinePaymentsProvider)
    patch.onlinePaymentsProvider = next.onlinePaymentsProvider;
  if (initial.siblingDiscountPercent !== next.siblingDiscountPercent)
    patch.siblingDiscountPercent = next.siblingDiscountPercent;
  if (initial.lateFeeRupees !== next.lateFeeRupees)
    patch.lateFeeAmountPaise = Math.round(next.lateFeeRupees * 100);
  if (initial.lateFeeGraceDays !== next.lateFeeGraceDays)
    patch.lateFeeGraceDays = next.lateFeeGraceDays;
  if (initial.feeDueDayOfMonth !== next.feeDueDayOfMonth)
    patch.feeDueDayOfMonth = Number(next.feeDueDayOfMonth);
  if (initial.automaticEmailsEnabled !== next.automaticEmailsEnabled)
    patch.automaticEmailsEnabled = next.automaticEmailsEnabled;
  return patch;
};

function SystemSettingsPage() {
  const isSuperAdmin = useAuthStore((s) => s.hasRole('super_admin'));
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const settingsQuery = useQuery({
    queryKey: ['system-settings'] as const,
    queryFn: ({ signal }) =>
      apiRequest<SystemSettingsRecord>('/api/system/settings', { signal }),
  });

  return (
    <ListShell
      title="System settings"
      description="Email, monthly fees and salaries, fee reminders, payments and fee rules."
    >
      {settingsQuery.isError ? (
        <ErrorState
          title="Could not load system settings"
          description={
            settingsQuery.error instanceof ApiError
              ? settingsQuery.error.message
              : 'Please try again in a moment.'
          }
          onRetry={() => settingsQuery.refetch()}
        />
      ) : settingsQuery.isLoading || !settingsQuery.data ? (
        <div className="space-y-4">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-44 w-full" />
        </div>
      ) : (
        <SettingsForm
          initial={settingsQuery.data}
          readOnly={!isSuperAdmin}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['system-settings'] });
            void queryClient.invalidateQueries({ queryKey: EMAIL_STATUS_KEY });
            void queryClient.invalidateQueries({ queryKey: ['announcements', 'email-available'] });
            toast('Settings saved.', { variant: 'success' });
          }}
        />
      )}
    </ListShell>
  );
}

interface SettingsFormProps {
  initial: SystemSettingsRecord;
  readOnly: boolean;
  onSaved: () => void;
}

function SettingsForm({ initial, readOnly, onSaved }: SettingsFormProps) {
  const jobsPath = useMenuPath('/system/scheduled-jobs');
  const { toast } = useToast();
  const initialValues = useMemo(() => toFormValues(initial), [initial]);

  // We deliberately do not wire a zod resolver here because
  // `systemSettingsUpdateSchema` is fully partial. The form holds the complete
  // set of fields and we validate the computed PATCH payload at submit time.
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  // Track our own dirty state so array fields (autoFeeReminderSchedule) work
  // — RHF's dirtyFields can mis-report for arrays mutated via Controller.
  const watched = watch();
  const computedPatch = useMemo<SystemSettingsUpdate>(() => {
    return buildPatch(initialValues, watched);
  }, [initialValues, watched]);
  const isDirty = Object.keys(computedPatch).length > 0;

  const mutation = useMutation({
    mutationFn: (body: SystemSettingsUpdate) =>
      apiRequest<{ ok: true }>('/api/system/settings', { method: 'PATCH', body }),
  });

  const submit = handleSubmit(async () => {
    if (Object.keys(computedPatch).length === 0) {
      toast('No changes to save.', { variant: 'info' });
      return;
    }
    try {
      const parsed = systemSettingsUpdateSchema.parse(computedPatch);
      await mutation.mutateAsync(parsed);
      onSaved();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Could not save settings.';
      toast(message, { variant: 'error' });
    }
  });

  const watchedFeeAuto = watch('autoGenerateFeeCyclesEnabled');
  const watchedSalaryAuto = watch('autoGenerateSalaryCyclesEnabled');
  const watchedRemindersOn = watch('autoFeeRemindersEnabled');
  const watchedFeeDay = watch('autoGenerateFeeCyclesDayOfMonth');
  const watchedDueDay = watch('feeDueDayOfMonth');
  const watchedPaymentsOn = watch('onlinePaymentsEnabled');

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      {readOnly ? (
        <div
          role="status"
          className="rounded-md border border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning)]/[0.08] px-4 py-3 text-sm text-[color:var(--color-text)]"
        >
          You can view system settings but only super admins can change them.
        </div>
      ) : null}

      <Card title="Email" description="Emails the portal sends by itself. Portal notifications always go out, whether this is on or off.">
        <Controller
          control={control}
          name="automaticEmailsEnabled"
          render={({ field }) => (
            <ToggleRow
              id="emails-on"
              label="Send automatic emails"
              description="Fee reminders, attendance reminders, announcement emails, the probation review summary, alerts about scheduled jobs, and website contact and enquiry messages to the office. While this is off, these emails aren’t sent or saved to send later."
              checked={field.value}
              onChange={field.onChange}
              disabled={readOnly}
            />
          )}
        />
        <EmailStatusPanel canTest={!readOnly} />
        <p className="text-xs text-[color:var(--color-text-muted)]">
          Sign-in codes, password resets and invitations are always sent, even when this is off.
        </p>
      </Card>

      <Card title="Monthly fees, salaries and reminders" description={`Create each month's fees and salaries automatically, and choose when families are reminded about fees. ${jobsPath ? `The time of day for each is set in ${jobsPath}.` : 'A super admin sets the time of day for each.'}`}>
        <Controller
          control={control}
          name="autoGenerateFeeCyclesEnabled"
          render={({ field }) => (
            <ToggleRow
              id="fee-auto"
              label="Create each month's fees automatically"
              description="On the day below, every active student gets this month's fee."
              checked={field.value}
              onChange={field.onChange}
              disabled={readOnly}
            />
          )}
        />
        <TextField
          label="Create fees on day"
          type="number"
          min={1}
          max={28}
          hint="Any day from 1 to 28."
          disabled={readOnly || !watchedFeeAuto}
          error={errors.autoGenerateFeeCyclesDayOfMonth?.message}
          {...register('autoGenerateFeeCyclesDayOfMonth', { valueAsNumber: true })}
        />
        <TextField
          label="Fees are due on day"
          type="number"
          min={1}
          max={28}
          hint={
            watchedFeeAuto && Number(watchedDueDay) < Number(watchedFeeDay)
              ? `Fees would be due before they're created on the ${ordinalDay(Number(watchedFeeDay))}. Choose a later day.`
              : 'Any day from 1 to 28. Reminders count from this day. Changes apply to next month’s fees.'
          }
          disabled={readOnly}
          error={errors.feeDueDayOfMonth?.message}
          {...register('feeDueDayOfMonth', {
            valueAsNumber: true,
            // The form is noValidate, so the rule needs its own message to show.
            validate: (v) => (Number.isInteger(v) && v >= 1 && v <= 28) || 'Choose a day from 1 to 28',
          })}
        />

        <Divider />

        <Controller
          control={control}
          name="autoGenerateSalaryCyclesEnabled"
          render={({ field }) => (
            <ToggleRow
              id="salary-auto"
              label="Create each month's salaries automatically"
              description="On the day below, every teacher and nalim gets this month's salary entry."
              checked={field.value}
              onChange={field.onChange}
              disabled={readOnly}
            />
          )}
        />
        <TextField
          label="Create salaries on day"
          type="number"
          min={1}
          max={28}
          hint="Any day from 1 to 28."
          disabled={readOnly || !watchedSalaryAuto}
          error={errors.autoGenerateSalaryCyclesDayOfMonth?.message}
          {...register('autoGenerateSalaryCyclesDayOfMonth', { valueAsNumber: true })}
        />

        <Divider />

        <Controller
          control={control}
          name="autoFeeRemindersEnabled"
          render={({ field }) => (
            <ToggleRow
              id="reminders-on"
              label="Send fee reminders"
              description="Families get a portal notification on the days below, and an email when automatic emails are on."
              checked={field.value}
              onChange={field.onChange}
              disabled={readOnly}
            />
          )}
        />
        <Controller
          control={control}
          name="autoFeeReminderSchedule"
          render={({ field }) => (
            <ReminderScheduleEditor
              value={field.value}
              onChange={field.onChange}
              disabled={readOnly || !watchedRemindersOn}
              feesCreatedOnDay={watchedFeeAuto ? Number(watchedFeeDay) || null : null}
              dueDay={Number(watchedDueDay) || FEE_DUE_DAY_OF_MONTH}
            />
          )}
        />
      </Card>

      <Card title="Payments" description="Whether parents can pay online and which provider handles the transaction.">
        <Controller
          control={control}
          name="onlinePaymentsEnabled"
          render={({ field }) => (
            <ToggleRow
              id="payments-on"
              label="Online payments enabled"
              description="When off, parents can only report payments they made another way."
              checked={field.value}
              onChange={field.onChange}
              disabled={readOnly}
            />
          )}
        />
        <fieldset disabled={readOnly || !watchedPaymentsOn}>
          <legend className="text-sm font-medium">Payment provider</legend>
          <div className="mt-2 flex flex-wrap gap-3">
            <RadioOption
              {...register('onlinePaymentsProvider')}
              value="none"
              label="None (manual reconcile)"
            />
            <RadioOption
              {...register('onlinePaymentsProvider')}
              value="razorpay"
              label="Razorpay"
            />
          </div>
          {errors.onlinePaymentsProvider ? (
            <p role="alert" className="mt-1 text-sm text-danger">
              {errors.onlinePaymentsProvider.message}
            </p>
          ) : null}
        </fieldset>
      </Card>

      <Card title="Discounts and fees" description="Sibling discount applied during fee generation, plus the late-fee policy.">
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            label="Sibling discount (%)"
            type="number"
            min={0}
            max={50}
            hint="Applied to each additional child in the same family."
            disabled={readOnly}
            error={errors.siblingDiscountPercent?.message}
            {...register('siblingDiscountPercent', { valueAsNumber: true })}
          />
          <TextField
            label="Late fee amount (₹)"
            type="number"
            min={0}
            step={1}
            hint="Charged once a fee is overdue past the grace period."
            disabled={readOnly}
            error={errors.lateFeeRupees?.message}
            {...register('lateFeeRupees', { valueAsNumber: true })}
          />
          <TextField
            label="Grace days"
            type="number"
            min={0}
            max={30}
            hint="Days after due date before a late fee applies."
            disabled={readOnly}
            error={errors.lateFeeGraceDays?.message}
            {...register('lateFeeGraceDays', { valueAsNumber: true })}
          />
        </div>
      </Card>

      {!readOnly ? (
        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          {isDirty ? (
            <span className="mr-auto text-xs text-[color:var(--color-text-muted)]">
              Unsaved changes
            </span>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            disabled={!isDirty || isSubmitting}
            onClick={() => reset(initialValues)}
            icon={<RotateCcw size={14} aria-hidden="true" />}
          >
            Reset
          </Button>
          <Button
            type="submit"
            disabled={!isDirty}
            loading={isSubmitting || mutation.isPending}
            icon={<Save size={14} aria-hidden="true" />}
          >
            Save changes
          </Button>
        </div>
      ) : null}

    </form>
  );
}

// Tiny utility components below ----------------------------------------------

function Card({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <header className="border-b border-[color:var(--color-border)] px-5 py-4">
        <h2 className="font-display text-base font-semibold">{title}</h2>
        {description ? (
          <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">{description}</p>
        ) : null}
      </header>
      <div className="space-y-4 px-5 py-5">{children}</div>
    </section>
  );
}

function Divider() {
  return <div role="separator" className="border-t border-[color:var(--color-border)]" />;
}

interface ToggleRowProps {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

function ToggleRow({ id, label, description, checked, onChange, disabled }: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {description ? (
          <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">{description}</p>
        ) : null}
      </div>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={
          'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors ' +
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500 ' +
          'disabled:cursor-not-allowed disabled:opacity-50 ' +
          (checked ? 'bg-[color:var(--color-success)]' : 'bg-[color:var(--color-border)]')
        }
      >
        <span
          aria-hidden="true"
          className={
            'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ' +
            (checked ? 'translate-x-5' : 'translate-x-0.5')
          }
        />
      </button>
    </div>
  );
}

interface RadioOptionProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

const RadioOption = forwardRef<HTMLInputElement, RadioOptionProps>(function RadioOption(
  { label, ...rest },
  ref,
) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input
        ref={ref}
        type="radio"
        className="h-4 w-4 border-[color:var(--color-border)] text-gold-700 focus:ring-gold-500"
        {...rest}
      />
      {label}
    </label>
  );
});

interface ReminderEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
  /** Day the month's fees are created, or null when they're created by hand. */
  feesCreatedOnDay: number | null;
  /** Day of the month fees fall due. */
  dueDay: number;
}

function ReminderScheduleEditor({ value, onChange, disabled, feesCreatedOnDay, dueDay }: ReminderEditorProps) {
  const [days, setDays] = useState('');
  const [when, setWhen] = useState<FeeReminder['when']>('before');
  const [problem, setProblem] = useState<string | null>(null);
  const reminders = sortFeeReminders(value).map((code) => ({ code, reminder: parseFeeReminder(code)! }));

  const add = () => {
    const n = Number(days);
    const max = when === 'before' ? FEE_REMINDER_MAX_DAYS_BEFORE : FEE_REMINDER_MAX_DAYS_AFTER;
    if (!Number.isInteger(n) || n < 1 || n > max) {
      setProblem(`Choose 1 to ${max} days ${when} the due date.`);
      return;
    }
    const code = feeReminderCode({ when, days: n });
    if (value.includes(code)) {
      setProblem('That reminder is already on the list.');
      return;
    }
    if (value.length >= FEE_REMINDER_MAX) {
      setProblem(`You can add up to ${FEE_REMINDER_MAX} reminders.`);
      return;
    }
    onChange(sortFeeReminders([...value, code]));
    setDays('');
    setProblem(null);
  };

  const inputClass =
    'rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2 text-sm focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500 disabled:opacity-60';

  return (
    <div>
      <p className="text-sm font-medium">When to remind families</p>
      <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
        Fees are due on the {ordinalDay(dueDay)} of each month. Add up to {FEE_REMINDER_MAX} reminders, before or after
        the due date. Reminders after the due date only go to families who haven’t paid yet.
      </p>

      {reminders.length === 0 ? (
        <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">No reminders chosen, so families won’t get fee reminders.</p>
      ) : (
        <ul className="mt-2 divide-y divide-dashed divide-[color:var(--color-border)] rounded-md border border-[color:var(--color-border)]">
          {reminders.map(({ code, reminder }) => {
            const warning = feeReminderWarning(reminder, { feesCreatedOnDay, dueDay });
            return (
              <li key={code} className="px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm">{describeFeeReminder(reminder)}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={disabled}
                    onClick={() => onChange(value.filter((v) => v !== code))}
                    aria-label={`Remove the reminder ${describeFeeReminder(reminder)}`}
                  >
                    Remove
                  </Button>
                </div>
                {warning ? <p className="mt-0.5 text-xs text-[color:var(--color-warning)]">{warning}</p> : null}
              </li>
            );
          })}
        </ul>
      )}

      <fieldset className="mt-3" disabled={disabled}>
        <legend className="mb-1 text-xs font-medium text-[color:var(--color-text-muted)]">Add a reminder</legend>
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="reminder-days">
            Number of days
          </label>
          <input
            id="reminder-days"
            type="number"
            inputMode="numeric"
            min={1}
            max={when === 'before' ? FEE_REMINDER_MAX_DAYS_BEFORE : FEE_REMINDER_MAX_DAYS_AFTER}
            value={days}
            placeholder="7"
            onChange={(e) => {
              setDays(e.target.value);
              setProblem(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            className={`${inputClass} w-20`}
          />
          <label className="sr-only" htmlFor="reminder-when">
            Before or after the due date
          </label>
          <select
            id="reminder-when"
            value={when}
            onChange={(e) => {
              setWhen(e.target.value as FeeReminder['when']);
              setProblem(null);
            }}
            className={inputClass}
          >
            <option value="before">days before the due date</option>
            <option value="after">days after the due date</option>
          </select>
          <Button type="button" size="sm" variant="secondary" onClick={add}>
            Add
          </Button>
        </div>
        {problem ? (
          <p role="alert" className="mt-1 text-xs text-[color:var(--color-danger)]">
            {problem}
          </p>
        ) : null}
      </fieldset>
    </div>
  );
}

/** 1 → "1st", 5 → "5th" */
const ordinalDay = (n: number): string => {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
};

